/**
 * API Client for Sovrun Backend
 *
 * Provides typed methods for interacting with the strategies,
 * allocations, and portfolio API endpoints.
 *
 * SFWL-041: split from a single ~1,436-line `app/lib/api.ts` into this
 * directory of domain modules. This barrel re-exports the exact same public
 * surface the old file had — every `@/lib/api` import site keeps working
 * with zero changes. See `./shared.ts` for the cross-module fetch/response
 * helpers and tenant-context state.
 */

// Tenant context
export { setActiveTenantId } from "./shared";

// Billing error type
export { ApiError } from "./shared";

// Strategy API
export { strategiesApi } from "./strategies";

// Allocations API
export { allocationsApi } from "./allocations";

// Orders API (CQG Integration)
export { ordersApi } from "./orders";

// Positions API (CQG Integration)
export { positionsApi } from "./positions";

// Smart Orders API (CQG Integration)
export { smartOrdersApi } from "./smart-orders";

// Execution Status API
export { executionStatusApi } from "./execution-status";

// Organizations API (Custom Hierarchy Endpoints)
export type { OrgRecord, MemberRecord } from "./organizations";
export { organizationsApi } from "./organizations";

// Portfolio Builder API
export { portfolioBuilderApi } from "./portfolio-builder";

// Market Data API
export type {
  AssetClass,
  AssetDto,
  AssetsListResponse,
  Timeframe,
  MarketDataCandle,
  TenantCandlesResponse,
  MarketDataProviderInfo,
  ProvidersListResponse,
  MarketDataCredentialStatus,
  MarketDataCredentialDto,
  CredentialsListResponse,
  ValidateCredentialResponse,
} from "./market-data";
export { marketDataApi } from "./market-data";

// Broker Credentials API (SFWL-006 Phase 2)
export type {
  BrokerId,
  BrokerCredentialDto,
  BrokerCredentialsListResponse,
} from "./broker-credentials";
export { brokerCredentialsApi } from "./broker-credentials";

// Block Accounts API (SFWL-012)
export type {
  Fcm,
  SmaStatus,
  SubManagedAccountDto,
  BlockAccountDto,
  BlockAccountsListResponse,
  CreateBlockAccountRequest,
  PatchBlockAccountRequest,
  CreateSmaRequest,
  PatchSmaRequest,
  LinkPaperAccountRequest,
  SmaExportRow,
  RunDailyExportResult,
  ExportConfig,
} from "./block-accounts";
export { blockAccountsApi } from "./block-accounts";

// Strategy Pricing Admin API (SFWL-014)
export type {
  StrategyPricingTier,
  StrategyPricingPromo,
  StrategyPricingOverrideDto,
  StrategyPricingVersionDto,
  StrategyPricingAdminResponse,
  SetPricingOverrideRequest,
  UpdateStrategyPricingRequest,
} from "./strategy-pricing";
export { strategyPricingApi } from "./strategy-pricing";

// Billing / Invoices API (SFWL-015)
export type {
  InvoiceStatus,
  InvoiceRelationship,
  InvoiceListItemDto,
  InvoiceLineItemDto,
  InvoiceBillTo,
  InvoiceIssuer,
  InvoiceDetailDto,
} from "./invoices";
export { invoicesApi } from "./invoices";

// ============================================================================
// Combined API Export
// ============================================================================

import { strategiesApi } from "./strategies";
import { allocationsApi } from "./allocations";
import { ordersApi } from "./orders";
import { positionsApi } from "./positions";
import { smartOrdersApi } from "./smart-orders";
import { executionStatusApi } from "./execution-status";
import { organizationsApi } from "./organizations";
import { portfolioBuilderApi } from "./portfolio-builder";
import { marketDataApi } from "./market-data";
import { brokerCredentialsApi } from "./broker-credentials";
import { blockAccountsApi } from "./block-accounts";
import { strategyPricingApi } from "./strategy-pricing";
import { invoicesApi } from "./invoices";

export const api = {
  strategies: strategiesApi,
  allocations: allocationsApi,
  orders: ordersApi,
  positions: positionsApi,
  smartOrders: smartOrdersApi,
  executionStatus: executionStatusApi,
  organizations: organizationsApi,
  portfolioBuilder: portfolioBuilderApi,
  marketData: marketDataApi,
  brokerCredentials: brokerCredentialsApi,
  blockAccounts: blockAccountsApi,
  strategyPricing: strategyPricingApi,
  invoices: invoicesApi,
};
